import { Component, OnInit, inject, signal } from '@angular/core';
import { MapComponent } from '../../components/map/map.component';
import { CardComponent } from '../../components/card/card.component';
import { BulletComponent } from '../../components/bullet/bullet.component';
import { RibbonComponent } from '../../components/ribbon/ribbon.component';
import { ButtonComponent } from '../../components/button/button.component';
import { FileUploadComponent } from '../../components/file-upload/file-upload.component';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { PortalService } from '../../api/portal/portal.service';
import { firstValueFrom } from 'rxjs';
import { OSMResponse } from '../../api/portal/portal.service.interface';
import { IconComponent } from '../../components/icon/icon.component';
import { NgStyle } from '@angular/common';
import { CardTitleComponent } from '../../components/card-title/card-title.component';
import { FeedbackCardComponent } from '../../components/feedback-card/feedback-card.component';
import { LinkComponent } from '../../components/link/link.component';
import { DownloadUtil } from '../../utils/download/download';
import { AnalyticsService } from '../../state/analytics/analytics.service';
import { ToastService } from '../../state/toast/toast.service';
import { getErrorMessage } from '../../utils/error-handler/error-handler';
import { validUpload } from '../../utils/upload/upload';

@Component({
  selector: 'app-osm-page',
  imports: [
    MapComponent,
    CardComponent,
    BulletComponent,
    RibbonComponent,
    ButtonComponent,
    FileUploadComponent,
    ReactiveFormsModule,
    IconComponent,
    NgStyle,
    CardTitleComponent,
    FeedbackCardComponent,
    LinkComponent,
  ],
  templateUrl: './osm-page.component.html',
  styleUrl: './osm-page.component.css',
})
export class OSMPageComponent implements OnInit {
  private portal = inject(PortalService);
  private fb = inject(FormBuilder);
  private analytics = inject(AnalyticsService);
  private toast = inject(ToastService);

  protected fileForm!: FormGroup;
  protected exampleForm!: FormGroup;
  protected mapFeatures = signal<OSMResponse | null | 'loading' | 'no-data'>(null);
  protected mapHeight = '800px';

  ngOnInit() {
    this.fileForm = this.fb.group({
      file: [null, Validators.required],
    });
    this.exampleForm = this.fb.group({});
  }

  async uploadFile() {
    const file: File = this.fileForm.value.file;
    if (!(await validUpload(file, this.toast))) return;
    this.track('Find Objects');
    this.mapFeatures.set('loading');
    try {
      const res = await firstValueFrom(this.portal.uploadOSMFile(file));
      this.mapFeatures.set(!res.features || res.features.length === 0 ? 'no-data' : res);
    } catch (error) {
      this.toast.showToast({ type: 'error', message: getErrorMessage(error), duration: 0 });
      this.mapFeatures.set('no-data');
    }
  }

  async exampleFile() {
    this.track('Try our example field');
    this.mapFeatures.set('loading');
    const res = await firstValueFrom(this.portal.exampleOSMFile());
    this.mapFeatures.set(res);
  }

  async download() {
    this.track('Download');
    const file: File = this.fileForm.value.file;
    const blob = file
      ? await firstValueFrom(this.portal.downloadOSMFile(file))
      : await firstValueFrom(this.portal.downloadExampleOSMFile());
    DownloadUtil.downloadFile(blob, 'osm.zip');
  }

  goToFeedback() {
    const location = window.location.href;
    return location.includes('#') ? location : location + '#feedback';
  }

  private track(event: string) {
    this.analytics.trackEvent('public-landscape-objects', event);
  }
}
